import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { combineQueries } from '@datorama/akita';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { OrderQuery } from '@modules/order/state/order-query';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { SoftwareOptions } from '@shared/models/enums/enums';
import { CaseTypeEnum } from '@modules/order/models/case-type.enum';
import { SendToTypeEnum } from '@modules/order/models/send-to-type.enum';
import { ProcedureMap } from '@shared/models/procedure-map';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { ApplianceDetailsUtils } from '@modules/appliance-details/utils/appliance-details-utils';
import { DentureDetailsUtils } from '@modules/denture-details/utils/denture-details-utils';
import type { Maybe } from '@/types/globals';

@Injectable({
	providedIn: 'root'
})
export class ProcedureSectionVisibilityService {
	isInvisalignDetailsVisible$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		map(pm => (pm?.ProcedureId as ProcedureEnum) === ProcedureEnum.Invisalign)
	);

	isSendToIDSMessageVisible$: Observable<boolean> = combineQueries([
		this.orderQuery.procedureMap$,
		this.orderQuery.availableProcedureMaps$,
		this.shellQuery.getReadOnlyState('order')
	]).pipe(
		map(([procedureMap, availableProcedureMaps, isReadOnly]) => {
			const isProcedureQualified =
				!!procedureMap &&
				(procedureMap.ProcedureId as ProcedureEnum) === ProcedureEnum.StudyModel_iRecord &&
				(procedureMap.SendToTypes as SendToTypeEnum) !== SendToTypeEnum.chairsideExocad &&
				(procedureMap.SendToTypes as SendToTypeEnum) !== SendToTypeEnum.chairsideGlidewell;

			const hasInvisalignOption =
				availableProcedureMaps?.some(pm => (pm.ProcedureId as ProcedureEnum) === ProcedureEnum.Invisalign) ?? false;

			return isReadOnly ? isProcedureQualified : isProcedureQualified && hasInvisalignOption;
		})
	);

	isApplianceDetailsVisible$: Observable<boolean> = combineLatest([
		this.orderQuery.procedureMap$,
		this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.EnableApplianceDetails)
	]).pipe(
		map(([procedureMap, hasCompanySwo]) => {
			return hasCompanySwo && ApplianceDetailsUtils.isApplianceDetailsEnabled(procedureMap?.ProcedureId);
		})
	);

	/** Appliance card with procedure Type — visible for Appliance whether or not EnableApplianceDetails SWO is on. */
	isApplianceProcedureSectionVisible$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		map(procedureMap => ApplianceDetailsUtils.isApplianceDetailsEnabled(procedureMap?.ProcedureId))
	);

	isDentureDetailsVisible$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		map(procedureMap => DentureDetailsUtils.isDentureDetailsEnabled(procedureMap?.ProcedureId))
	);

	isScanOptionsVisible$: Observable<boolean> = combineQueries([
		this.shellQuery.isScanOptionsVisible$,
		this.orderQuery.procedureMap$
	]).pipe(
		map(
			([isScanOptionsVisible, procedureMap]) =>
				isScanOptionsVisible ||
				!!(procedureMap?.MultiBiteIsEnabled || procedureMap?.DentureCopyScanIsEnabled || procedureMap?.PreTreatmentScanIsEnabled)
		)
	);

	/** Visible when procedure is ImplantPlanning (scan for surgical guide). */
	isScanSurgicalGuideVisible$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		map(procedureMap => procedureMap?.ProcedureId === ProcedureEnum.ImplantPlanning)
	);

	isTeethDiagramAreaVisible$: Observable<boolean> = combineQueries([
		this.shellQuery.isProcedureFlow$,
		this.orderQuery.caseType$,
		this.orderQuery.procedureMap$
	]).pipe(
		map(([isProcedureFlow, caseTypeSelected, procedureMap]: [boolean, Maybe<{ Id: number }>, Maybe<ProcedureMap>]) => {
			if (isProcedureFlow && procedureMap) {
				return procedureMap.ToothDiagramIsEnabled;
			}

			const caseTypeId = caseTypeSelected?.Id;

			return (
				caseTypeId !== undefined &&
				[
					CaseTypeEnum.Restorative,
					CaseTypeEnum.ChairSideMilling,
					CaseTypeEnum.ChairSideMillingGlidewellIo,
					CaseTypeEnum.ChairSideMillingCerec
				].includes(caseTypeId as CaseTypeEnum)
			);
		})
	);

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery,
		private readonly softwareOptionsService: SoftwareOptionsService
	) {}
}
